import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN, pricePerKg } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, ArrowLeft, Truck, Flame, Award, ShieldCheck } from "lucide-react";
import ComboSuggestions from "@/components/ComboSuggestions";
import WishlistButton from "@/components/WishlistButton";
import SafeImage from "@/components/SafeImage";
import { useSEO, buildProductLd } from "@/lib/seo";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [combos, setCombos] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    setCombos([]);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const firstVariant = r.data.variants?.[0];
      setVariantId(firstVariant?.id || null);
      setQty(1);
      setActiveImage(0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = (product.images && product.images.length ? product.images : [product.image]).filter(Boolean);
    return imgs.length ? imgs : [product.image];
  }, [product]);

  const selectedVariant = useMemo(() => {
    if (!product?.variants?.length) return null;
    return product.variants.find((v) => v.id === variantId) || product.variants[0];
  }, [product, variantId]);

  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;
  const hasMultipleVariants = (product?.variants?.length || 0) > 1;

  useSEO(
    product
      ? {
          title: product.name,
          description: product.short_desc || product.description?.slice(0, 160),
          image: product.image,
          jsonLd: buildProductLd({
            name: product.name,
            description: product.short_desc || product.description || "",
            image: product.image,
            slug: product.slug,
            price: displayPrice,
            sku: product.sku || product.id,
          }),
        }
      : { title: "Cargando..." }
  );

  const combosTotal = combos.reduce((s, p) => s + (p.variants?.[0]?.price ?? p.price), 0);
  const grandTotal = displayPrice * qty + combosTotal;

  const onCombosChange = useCallback((list) => setCombos(list), []);

  const handleAddToCart = () => {
    // main product
    const vName = selectedVariant
      ? `${product.name} — ${selectedVariant.label}`
      : product.name;
    addItem({
      type: "product",
      id: product.id,
      variant_id: selectedVariant?.id || null,
      name: vName,
      price: displayPrice,
      image: product.image,
      unit: selectedVariant?.label || product.unit,
      quantity: qty,
    });
    // combos — use first variant by default
    combos.forEach((p) => {
      const v = p.variants?.[0];
      addItem({
        type: "product",
        id: p.id,
        variant_id: v?.id || null,
        name: v && (p.variants?.length || 0) > 1 ? `${p.name} — ${v.label}` : p.name,
        price: v?.price ?? p.price,
        image: p.image,
        unit: v?.label || p.unit,
        quantity: 1,
      });
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="font-display text-2xl text-white tracking-wider">Cargando...</div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="text-center">
          <div className="font-display text-4xl text-white tracking-wider mb-4">No encontramos ese corte, compare</div>
          <Link to="/catalogo/cortes" className="text-arre-red hover:underline">Ver catálogo →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-arre-black min-h-screen" data-testid="product-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-8">
        <Link to={`/catalogo/${product.category}`} className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs tracking-widest uppercase" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Volver al catálogo
        </Link>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Image */}
          <div>
            <div className="bg-arre-surface aspect-[4/5] overflow-hidden border border-arre-border grain relative">
              <SafeImage src={gallery[activeImage] || product.image} alt={product.name} fallbackLabel={product.name} className="w-full h-full object-cover" />
              <div className="absolute top-4 right-4">
                <WishlistButton productId={product.id} className="bg-arre-black/60 backdrop-blur-sm w-10 h-10 flex items-center justify-center border border-arre-border" />
              </div>
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 flex-wrap" data-testid="product-gallery-thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 border overflow-hidden flex-shrink-0 transition ${
                      i === activeImage ? "border-arre-red" : "border-arre-border opacity-70 hover:opacity-100"
                    }`}
                    data-testid={`product-gallery-thumb-${i}`}
                  >
                    <SafeImage src={img} alt={`${product.name} ${i + 1}`} fallbackLabel={product.name} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">{product.category}</div>
            <h1 className="font-display text-6xl md:text-8xl tracking-wider uppercase text-white leading-[0.85]">
              {product.name}
            </h1>
            <p className="font-serif-display italic text-xl text-white/70 mt-4">{product.short_desc}</p>

            <div className="mt-8 py-6 border-y border-arre-border">
              <div className="text-xs tracking-widest uppercase text-white/50">
                {selectedVariant?.label || product.unit}
              </div>
              <div className="font-display text-6xl text-white mt-2" data-testid="product-price">
                {fmtMXN(displayPrice)}
              </div>
              {(() => {
                const w = selectedVariant?.weight_g ?? product.weight_g;
                const perKg = pricePerKg({ price: displayPrice, weight_g: w });
                return perKg ? (
                  <div className="text-sm tracking-widest uppercase text-white/60 mt-2" data-testid="product-price-per-kg">
                    Equivale a {perKg} / kg
                  </div>
                ) : null;
              })()}
            </div>

            {/* VARIANT SELECTOR */}
            {hasMultipleVariants && (
              <div className="mt-8" data-testid="variant-selector">
                <div className="text-xs tracking-[0.25em] uppercase text-white/60 mb-3">Elige tu porción</div>
                <div className="grid grid-cols-2 gap-3">
                  {product.variants.map((v) => {
                    const active = v.id === variantId;
                    const vPerKg = pricePerKg({ price: v.price, weight_g: v.weight_g });
                    return (
                      <button
                        key={v.id}
                        onClick={() => setVariantId(v.id)}
                        className={`border text-left p-4 transition ${
                          active ? "border-arre-red bg-arre-red/10" : "border-arre-border bg-arre-surface hover:border-white/40"
                        }`}
                        data-testid={`variant-${v.id}`}
                      >
                        <div className={`font-display text-xl tracking-wider uppercase ${active ? "text-arre-red" : "text-white"}`}>
                          {v.id === "350g" ? "350 g" : v.id === "1kg" ? "1 kg" : v.label.split(" ")[0] + " " + (v.label.split(" ")[1] || "")}
                        </div>
                        <div className="text-xs text-white/60 mt-1">{v.label}</div>
                        <div className="font-display text-2xl text-white mt-2">{fmtMXN(v.price)}</div>
                        {vPerKg && (
                          <div className="text-[10px] tracking-widest uppercase text-white/50 mt-1">
                            {vPerKg} / kg
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-white/80 leading-relaxed mt-8">{product.description}</p>

            {/* COMBO SUGGESTIONS */}
            <ComboSuggestions slug={product.slug} kind="product" onChange={onCombosChange} />

            {/* Qty + CTA */}
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <div className="flex items-center border border-arre-border bg-arre-surface">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-5 py-4 text-white hover:bg-arre-black"
                  data-testid="qty-minus"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-6 font-display text-2xl text-white" data-testid="qty-value">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-5 py-4 text-white hover:bg-arre-black"
                  data-testid="qty-plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                className="flex-1 btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase px-8 py-4"
                data-testid="add-to-cart-btn"
              >
                {combos.length > 0
                  ? `Agregar combo (${combos.length + 1} items) — ${fmtMXN(grandTotal)}`
                  : `Agregar al carrito — ${fmtMXN(displayPrice * qty)}`}
              </button>
            </div>

            {combos.length > 0 && (
              <p className="text-xs text-white/50 mt-3" data-testid="combo-summary">
                Incluye: {product.name}{selectedVariant && hasMultipleVariants ? ` (${selectedVariant.label.split(" ")[0]} ${selectedVariant.label.split(" ")[1] || ""})` : ""} ×{qty}{" "}
                {combos.map((c) => `+ ${c.name}`).join(" ")}
              </p>
            )}

            {/* Trust strip */}
            <div className="grid grid-cols-2 gap-4 mt-10">
              {[
                { icon: Award, label: "100% Sonora", sub: "Calidad premium" },
                { icon: Truck, label: "Envío refrigerado", sub: "Frescura asegurada" },
                { icon: Flame, label: "Listo para asar", sub: "Corte boutique" },
                { icon: ShieldCheck, label: "Garantía total", sub: "Satisfacción o reembolso" },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-3 p-4 border border-arre-border bg-arre-surface/40">
                  <t.icon className="w-5 h-5 text-arre-red flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-display text-sm tracking-widest uppercase text-white">{t.label}</div>
                    <div className="text-xs text-white/50">{t.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
